import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from 'mobx';
import { NotificationManager } from '../components/notification-manager';
import { HttpMethod } from '../types';
import {
    Doc31AuditEvent,
    Doc31AuditEventPage,
    Doc31Configuration,
    Doc31ConfigurationSection,
    Doc31ConfigurationView,
    Doc31CouponPromotion,
    Doc31CouponPromotionCreate,
    Doc31CouponPromotionPatch,
    Doc31EditionState,
    Doc31EditionStatus,
    Doc31EditionSummary,
    Doc31ValidationIssue,
    Doc31ValidationResult,
} from '../types/doc31';
import type { NetworkCallResult, RootStore } from './root';

/** Server codes the screens answer beyond the toast. */
export const DOC31_VERSION_MISMATCH = 'doc31_configuration_version_mismatch';
export const DOC31_CONFIGURATION_INVALID = 'doc31_configuration_invalid';
/** Client-side: the reply came back after the operator switched edition. */
export const DOC31_EDITION_LEFT = 'doc31_edition_left';
/** The edition's status moved elsewhere, so the write was refused. */
export const DOC31_EDITION_READ_ONLY = 'doc31_edition_read_only';
export const DOC31_TRANSITION_REFUSED = 'doc31_transition_refused';

export type Doc31WriteResult<T> =
    | { ok: true; data: T }
    | {
          ok: false;
          code?: string;
          message?: string;
          /**
           * Why a configuration save was refused: the 422 check, or the
           * field issues of a 400 (`window_order`).
           */
          validation?: Doc31ValidationResult;
      };

const HISTORY_PAGE_SIZE = 50;

/** A 400 lists the fields it refused as issues carrying a `path`. */
function isIssueList(details: unknown): details is Doc31ValidationIssue[] {
    return (
        Array.isArray(details) &&
        details.length > 0 &&
        details.every(
            (issue: unknown) =>
                typeof issue === 'object' &&
                issue !== null &&
                'path' in issue &&
                typeof issue.path === 'string'
        )
    );
}

function toWriteResult<T>(
    response: NetworkCallResult<T>,
    configurationVersion: number | undefined
): Doc31WriteResult<T> {
    if (!response.err) {
        return { ok: true, data: response.data };
    }
    if (response.cancelled) {
        return { ok: false };
    }
    const { code, message, details, status } = response;
    let validation: Doc31ValidationResult | undefined;
    if (code === DOC31_CONFIGURATION_INVALID) {
        validation = details as Doc31ValidationResult;
    } else if (status === 400 && isIssueList(details)) {
        validation = {
            valid: false,
            errors: details,
            warnings: [],
            configurationVersion: configurationVersion ?? 0,
        };
    }
    return { ok: false, code, message, validation };
}

/**
 * One 31DOC edition at a time, picked by key (DOC31-33). Every call names the
 * edition in `X-Edition-Key`; a lifecycle move or a withdrawal also carries
 * the operator's reason in `X-Command-Reason`. A response for an edition the
 * operator has since left is dropped.
 */
export class Doc31Store {
    @observable editions: Doc31EditionSummary[] = [];
    @observable editionKey: string | null = null;
    @observable view: Doc31ConfigurationView | null = null;
    @observable state: Doc31EditionState | null = null;
    @observable coupons: Doc31CouponPromotion[] = [];
    @observable history: Doc31AuditEvent[] = [];
    @observable historyCursor: string | null = null;
    @observable hasMoreHistory = true;
    /** The last coupons read failed: an empty list would be a lie. */
    @observable couponsLoadFailed = false;
    @observable historyLoadFailed = false;
    /** Someone saved the configuration after this screen loaded it. */
    @observable isConfigurationStale = false;
    @observable isLoadingEdition = false;
    @observable isLoadingHistory = false;
    @observable isSaving = false;
    /** Successful writes so far; History refetches when it moves. */
    @observable changeCount = 0;
    /** Explicit reloads so far; Setup drops every unsaved edit when it moves. */
    @observable reloadCount = 0;
    /**
     * Play dates being edited in Setup, not yet saved: the page header
     * previews them. Null shows the saved window.
     */
    @observable draftPlayWindow: {
        playStartsAt: string;
        playEndsAt: string;
    } | null = null;
    /**
     * Play days being edited in Game rules, not yet saved (null: cleared,
     * so 31). Undefined shows the saved value.
     */
    @observable draftMaxPlayDays: number | null | undefined = undefined;

    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        makeObservable(this);
        this.rootStore = rootStore;
    }

    @action
    setDraftPlayWindow(
        window: { playStartsAt: string; playEndsAt: string } | null
    ): void {
        this.draftPlayWindow = window;
    }

    @action
    setDraftMaxPlayDays(days: number | null | undefined): void {
        this.draftMaxPlayDays = days;
    }

    @computed get status(): Doc31EditionStatus | null {
        return this.state?.status ?? this.view?.status ?? null;
    }

    /** Earning is over: nothing can change any more. */
    @computed get isReadOnly(): boolean {
        return (
            this.status === 'earning_closed' ||
            this.status === 'closed' ||
            this.status === 'archived'
        );
    }

    /** False when the list could not load (the call already toasted why). */
    @action
    async fetchEditions(this: Doc31Store): Promise<boolean> {
        const response = await this.rootStore.makeNetworkCall<
            Doc31EditionSummary[]
        >({ method: HttpMethod.GET, url: '/31doc/editions' });
        if (response.err) {
            return false;
        }
        runInAction(() => {
            this.editions = response.data;
        });
        return true;
    }

    /** Switches edition and loads its configuration, state and coupons. */
    @action
    async selectEdition(this: Doc31Store, key: string): Promise<void> {
        this.editionKey = key;
        this.view = null;
        this.state = null;
        this.coupons = [];
        this.history = [];
        this.historyCursor = null;
        this.hasMoreHistory = true;
        this.isConfigurationStale = false;
        this.couponsLoadFailed = false;
        this.historyLoadFailed = false;
        this.isLoadingEdition = true;
        try {
            await Promise.all([
                this.fetchConfiguration(),
                this.fetchState(),
                this.fetchCoupons(),
            ]);
        } finally {
            runInAction(() => {
                if (this.editionKey === key) {
                    this.isLoadingEdition = false;
                }
            });
        }
    }

    /** False when the configuration could not load (the call toasted why). */
    @action
    async fetchConfiguration(this: Doc31Store): Promise<boolean> {
        const response = await this.call<Doc31ConfigurationView>({
            method: HttpMethod.GET,
            url: '/31doc/configuration',
        });
        if (!response) {
            return false;
        }
        runInAction(() => {
            this.view = response;
            this.isConfigurationStale = false;
        });
        return true;
    }

    /**
     * The stale banner's Reload: the saved setup replaces every edit, but
     * only once it has loaded; a failed reload keeps the operator's edits.
     */
    @action
    async reload(this: Doc31Store): Promise<void> {
        if (!(await this.fetchConfiguration())) {
            return;
        }
        runInAction(() => {
            this.reloadCount += 1;
        });
    }

    @action
    async fetchState(this: Doc31Store): Promise<void> {
        const response = await this.call<Doc31EditionState>({
            method: HttpMethod.GET,
            url: '/31doc/state',
        });
        if (response) {
            runInAction(() => {
                this.state = response;
            });
        }
    }

    @action
    async fetchCoupons(this: Doc31Store): Promise<void> {
        const key = this.editionKey;
        const response = await this.call<Doc31CouponPromotion[]>({
            method: HttpMethod.GET,
            url: '/31doc/coupon-promotions',
        });
        if (this.editionKey !== key) {
            return;
        }
        runInAction(() => {
            this.couponsLoadFailed = !response;
            if (response) {
                this.coupons = response;
            }
        });
    }

    /** Newest first; `more` appends the next page. */
    @action
    async fetchHistory(
        this: Doc31Store,
        options: { more?: boolean } = {}
    ): Promise<void> {
        const cursor = options.more ? this.historyCursor : null;
        if (options.more && !cursor) {
            return;
        }
        const key = this.editionKey;
        this.isLoadingHistory = true;
        try {
            const page = await this.call<Doc31AuditEventPage>({
                method: HttpMethod.GET,
                url: '/31doc/history',
                params: {
                    limit: HISTORY_PAGE_SIZE,
                    ...(cursor ? { cursor } : {}),
                },
            });
            if (this.editionKey !== key) {
                return;
            }
            runInAction(() => {
                // A failed next page keeps the rows already shown.
                if (page || !options.more) {
                    this.historyLoadFailed = !page;
                }
                if (page) {
                    this.history = options.more
                        ? [...this.history, ...page.items]
                        : page.items;
                    this.historyCursor = page.nextCursor;
                    this.hasMoreHistory = page.nextCursor !== null;
                }
            });
        } finally {
            runInAction(() => {
                this.isLoadingHistory = false;
            });
        }
    }

    /**
     * Saves one section against the newest version this screen knows. A
     * newer save elsewhere marks the screen stale instead of overwriting it.
     */
    @action
    async saveConfigurationSection<S extends Doc31ConfigurationSection>(
        this: Doc31Store,
        section: S,
        value: Doc31Configuration[S],
        reason?: string
    ): Promise<Doc31WriteResult<Doc31ConfigurationView>> {
        const version = this.view?.configurationVersion;
        const result = await this.write<Doc31ConfigurationView>(
            {
                method: HttpMethod.PUT,
                url: `/31doc/configuration/${section}`,
                data: value,
                headers: { 'If-Match': `"${version}"` },
            },
            reason
        );
        if (result.ok) {
            runInAction(() => {
                this.view = result.data;
            });
            NotificationManager.showSuccess('Saved.');
            await this.fetchState();
        } else if (result.code === DOC31_VERSION_MISMATCH) {
            runInAction(() => {
                this.isConfigurationStale = true;
            });
        }
        return result;
    }

    @action
    async transition(
        this: Doc31Store,
        targetStatus: Doc31EditionStatus,
        reason: string
    ): Promise<Doc31WriteResult<Doc31EditionState>> {
        const result = await this.write<Doc31EditionState>(
            {
                method: HttpMethod.PUT,
                url: '/31doc/state',
                data: {
                    targetStatus,
                    configurationVersion:
                        this.view?.configurationVersion ??
                        this.state?.configurationVersion,
                },
            },
            reason
        );
        if (result.ok) {
            runInAction(() => {
                this.state = result.data;
            });
            await this.fetchConfiguration();
        } else if (result.code === DOC31_VERSION_MISMATCH) {
            runInAction(() => {
                this.isConfigurationStale = true;
            });
        } else if (result.validation) {
            const { validation } = result;
            runInAction(() => {
                if (this.view) {
                    this.view = { ...this.view, validation };
                }
            });
        }
        return result;
    }

    @action
    async createCoupon(
        this: Doc31Store,
        body: Doc31CouponPromotionCreate,
        reason?: string
    ): Promise<Doc31WriteResult<Doc31CouponPromotion>> {
        const result = await this.write<Doc31CouponPromotion>(
            {
                method: HttpMethod.POST,
                url: '/31doc/coupon-promotions',
                data: body,
            },
            reason
        );
        if (result.ok) {
            NotificationManager.showSuccess('Coupon created.');
            await this.refreshAfterCouponChange();
        }
        return result;
    }

    @action
    async updateCoupon(
        this: Doc31Store,
        promotionId: number,
        patch: Doc31CouponPromotionPatch,
        reason?: string
    ): Promise<Doc31WriteResult<Doc31CouponPromotion>> {
        const result = await this.write<Doc31CouponPromotion>(
            {
                method: HttpMethod.PATCH,
                url: `/31doc/coupon-promotions/${promotionId}`,
                data: patch,
            },
            reason
        );
        if (result.ok) {
            NotificationManager.showSuccess('Coupon saved.');
            await this.refreshAfterCouponChange();
        }
        return result;
    }

    @action
    async withdrawCoupon(
        this: Doc31Store,
        promotionId: number,
        reason: string
    ): Promise<Doc31WriteResult<Doc31CouponPromotion>> {
        const result = await this.write<Doc31CouponPromotion>(
            {
                method: HttpMethod.POST,
                url: `/31doc/coupon-promotions/${promotionId}/withdrawal`,
            },
            reason
        );
        if (result.ok) {
            NotificationManager.showSuccess('Coupon withdrawn.');
            await this.refreshAfterCouponChange();
        }
        return result;
    }

    /** Coupons feed the go-live check, so the configuration view follows. */
    private async refreshAfterCouponChange(this: Doc31Store): Promise<void> {
        await Promise.all([this.fetchCoupons(), this.fetchConfiguration()]);
    }

    /** A read for the current edition; null on failure or once left. */
    private async call<T>(
        this: Doc31Store,
        options: Parameters<RootStore['makeNetworkCall']>[0]
    ): Promise<T | null> {
        const key = this.editionKey;
        if (!key) {
            return null;
        }
        const response = await this.rootStore.makeNetworkCall<T>({
            ...options,
            headers: { ...options.headers, 'X-Edition-Key': key },
        });
        return !response.err && this.editionKey === key ? response.data : null;
    }

    private async write<T>(
        this: Doc31Store,
        options: Parameters<RootStore['makeNetworkCall']>[0],
        reason: string | undefined
    ): Promise<Doc31WriteResult<T>> {
        const key = this.editionKey;
        if (!key) {
            return { ok: false, message: 'Pick an edition first.' };
        }
        this.isSaving = true;
        try {
            const response: NetworkCallResult<T> = await this.rootStore.makeNetworkCall<
                T
            >({
                ...options,
                headers: {
                    ...options.headers,
                    'X-Edition-Key': key,
                    // Saves go without one; lifecycle moves and withdrawals
                    // ask the operator why.
                    ...(reason ? { 'X-Command-Reason': reason } : {}),
                },
            });
            // A reply for an edition the operator has since left must not
            // land on the one now shown.
            if (this.editionKey !== key) {
                return { ok: false, code: DOC31_EDITION_LEFT };
            }
            const result = toWriteResult(
                response,
                this.view?.configurationVersion
            );
            if (result.ok) {
                runInAction(() => {
                    this.changeCount += 1;
                });
            } else if (
                result.code === DOC31_EDITION_READ_ONLY ||
                result.code === DOC31_TRANSITION_REFUSED
            ) {
                // Another admin moved the status; transitions don't bump the
                // configuration version, so the stale banner can't tell.
                await Promise.all([
                    this.fetchState(),
                    this.fetchConfiguration(),
                ]);
            }
            return result;
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }
}
